/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * Security Events browser — /admin/security-events.
 * Filters: user email (regex), event type, IP, severity, date range.
 * Permission-gated by backend (audit.view). Frontend just hides the tile.
 */

import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';
import { Shield, Search, RefreshCw } from 'lucide-react';

const EVENT_TYPES = [
  '', 'AUTH_LOGIN_SUCCESS', 'AUTH_LOGIN_FAILED', 'ACCOUNT_LOCKED',
  'AUTH_LOGOUT', 'AUTH_TOKEN_REFRESH',
  'AUTH_PASSWORD_CHANGE', 'AUTH_PASSWORD_RESET_REQUEST', 'AUTH_PASSWORD_RESET_COMPLETE',
  'AUTH_EMAIL_VERIFIED',
  'AUTH_2FA_ENABLED', 'AUTH_2FA_DISABLED',
  'AUTH_2FA_VERIFICATION_SUCCESS', 'AUTH_2FA_VERIFICATION_FAILED',
  'AUTH_SESSIONS_REVOKED', 'AUTH_SECURITY_ALERT_SENT',
  'SECURITY_RATE_LIMIT_EXCEEDED', 'SECURITY_PERMISSION_DENIED',
  'USER_CONNECT_ACCESS_CHANGED', 'AUDIT_LOGS_PURGED'
];

const SEVERITIES = ['', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'];

const SecurityEvents = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [filters, setFilters] = useState({
    userEmail: '', eventType: '', ip: '', severity: '', from: '', to: ''
  });

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('page', String(page));
      params.set('limit', '50');
      const resp = await adminApi.request(`/admin/security-events?${params.toString()}`);
      if (resp.success) {
        setRows(resp.data.rows || []);
        setTotal(resp.data.total || 0);
        setPages(resp.data.pages || 1);
      } else {
        setError(resp.error || 'Failed to load');
      }
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const setFilter = (k, v) => { setPage(1); setFilters(f => ({ ...f, [k]: v })); };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—';

  return (
    <AdminLayout>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <Shield style={{ width: 24, height: 24, color: '#0891b2' }} />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Security Events</h1>
          <button onClick={fetchRows} title="Refresh"
                  style={{ marginLeft: 'auto', background: '#f1f5f9', border: '1px solid #e2e8f0',
                           padding: '6px 12px', borderRadius: 6, cursor: 'pointer', display: 'flex',
                           alignItems: 'center', gap: 6 }}>
            <RefreshCw style={{ width: 14, height: 14 }} /> Refresh
          </button>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                      padding: 16, marginBottom: 16, display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <input type="text" placeholder="User email (regex)"
                 value={filters.userEmail} onChange={e => setFilter('userEmail', e.target.value)}
                 style={inp} />
          <select value={filters.eventType} onChange={e => setFilter('eventType', e.target.value)} style={inp}>
            {EVENT_TYPES.map(t => <option key={t || 'ANY'} value={t}>{t || 'Any event type'}</option>)}
          </select>
          <input type="text" placeholder="IP address"
                 value={filters.ip} onChange={e => setFilter('ip', e.target.value)} style={inp} />
          <select value={filters.severity} onChange={e => setFilter('severity', e.target.value)} style={inp}>
            {SEVERITIES.map(s => <option key={s || 'ANY'} value={s}>{s || 'Any severity'}</option>)}
          </select>
          <input type="datetime-local" value={filters.from} onChange={e => setFilter('from', e.target.value)} style={inp} />
          <input type="datetime-local" value={filters.to}   onChange={e => setFilter('to', e.target.value)} style={inp} />
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
                        padding: 12, borderRadius: 8, marginBottom: 12 }}>{error}</div>
        )}

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', color: '#64748b', fontSize: 12, borderBottom: '1px solid #e2e8f0' }}>
            {loading ? 'Loading…' : `${total.toLocaleString()} events — page ${page} / ${pages}`}
          </div>
          <div style={{ maxHeight: '65vh', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                <tr>
                  {['When (IST)', 'Event', 'Severity', 'User', 'IP', 'Channel', 'Action'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r._id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={td}>{fmtDate(r.createdAt)}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.eventType}</td>
                    <td style={{ ...td, color: sevColor(r.severity) }}>{r.severity}</td>
                    <td style={td}>{r.username || '—'}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.ipAddress || '—'}</td>
                    <td style={td}>{r.metadata?.channel || '—'}</td>
                    <td style={td}>{r.action || '—'}</td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>
                    No events match the filters.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ padding: 12, display: 'flex', gap: 8, justifyContent: 'flex-end',
                        borderTop: '1px solid #e2e8f0' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={btn}>
              ← Prev
            </button>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} style={btn}>
              Next →
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

const inp = {
  padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6,
  fontSize: 13, outline: 'none'
};
const btn = {
  padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6,
  background: '#fff', cursor: 'pointer', fontSize: 13
};
const th = {
  padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#475569',
  fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4
};
const td = { padding: '8px 12px', color: '#334155', verticalAlign: 'top' };

function sevColor(s) {
  return s === 'CRITICAL' ? '#dc2626'
       : s === 'ERROR' ? '#ea580c'
       : s === 'WARNING' ? '#ca8a04'
       : '#334155';
}

export default SecurityEvents;
